import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Shield, Copy, Check, Eye, EyeOff, AlertCircle, Clock, ArrowLeft } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface FirstLoginMFASetupProps {
  onComplete: () => void;
  onSkip: () => void;
  authToken: string;
}

interface SetupData {
  qrCodeUrl: string;
  backupCodes: string[];
  manualEntryKey: string;
}

const FirstLoginMFASetup: React.FC<FirstLoginMFASetupProps> = ({ 
  onComplete, 
  onSkip, 
  authToken 
}) => {
  const [step, setStep] = useState<'intro' | 'setup' | 'verify' | 'backup'>('intro');
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState<boolean[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const initializeSetup = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[FirstLoginMFASetup] Initializing 2FA setup');
      console.log('[FirstLoginMFASetup] Auth token for setup:', authToken ? `${authToken.substring(0, 20)}...` : 'NO TOKEN');
      
      const response = await fetch(`${API_BASE_URL}/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('[FirstLoginMFASetup] Setup response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[FirstLoginMFASetup] Setup error:', errorData);
        throw new Error(errorData.error || 'Failed to setup 2FA');
      }

      const data = await response.json();
      setSetupData(data);
      setCopiedCodes(new Array(data.backupCodes.length).fill(false));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[FirstLoginMFASetup] Verifying with code:', verificationCode);
      console.log('[FirstLoginMFASetup] Using auth token:', authToken ? `${authToken.substring(0, 20)}...` : 'NO TOKEN');
      
      const response = await fetch(`${API_BASE_URL}/auth/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: verificationCode  // Backend expects 'token' not 'code'
        })
      });

      console.log('[FirstLoginMFASetup] Verify response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Verification failed';
        try {
          const errorData = await response.json();
          console.error('[FirstLoginMFASetup] Verify error:', errorData);
          errorMessage = errorData.error || errorData.message || 'Verification failed';
        } catch (parseError) {
          console.error('[FirstLoginMFASetup] Could not parse error response');
        }
        
        // Add more specific error handling
        if (response.status === 400) {
          if (errorMessage.includes('Invalid verification code')) {
            setError('Invalid code. Please check and try again.');
          } else if (errorMessage.includes('expired')) {
            setError('The code has expired. Please try again.');
          } else {
            setError(errorMessage);
          }
          return; // Don't throw, just show error
        }
        
        throw new Error(errorMessage);
      }

      // Successfully verified MFA
      const data = await response.json();
      console.log('[FirstLoginMFASetup] MFA verified successfully:', data);
      
      // If the backend returns user data and tokens, store them
      if (data.user && data.accessToken) {
        localStorage.setItem('riskninja-user', JSON.stringify(data.user));
        localStorage.setItem('riskninja-token', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('riskninja-refresh-token', data.refreshToken);
        }
      }
      
      setStep('backup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (index !== undefined) {
        const newCopiedCodes = [...copiedCodes];
        newCopiedCodes[index] = true;
        setCopiedCodes(newCopiedCodes);
        setTimeout(() => {
          const resetCopiedCodes = [...newCopiedCodes];
          resetCopiedCodes[index] = false;
          setCopiedCodes(resetCopiedCodes);
        }, 2000);
      } else {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const renderIntroStep = () => (
    <div className="text-center space-y-6">
      <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
        <Shield className="h-8 w-8 text-green-600" />
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Secure Your Account with Multi-Factor Authentication
        </h2>
        <p className="text-gray-600 mb-6">
          Add an extra layer of security to your account. We'll guide you through setting up 
          two-factor authentication using an authenticator app.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
        <h3 className="font-semibold text-blue-900 mb-3">What you'll need:</h3>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-center">
            <Check className="h-4 w-4 mr-2 text-blue-600" />
            Microsoft Authenticator app (recommended)
          </li>
          <li className="flex items-center">
            <Check className="h-4 w-4 mr-2 text-blue-600" />
            Your mobile device with camera for QR code
          </li>
          <li className="flex items-center">
            <Clock className="h-4 w-4 mr-2 text-blue-600" />
            About 3-5 minutes to complete setup
          </li>
        </ul>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => {
            setStep('setup');
            initializeSetup();
          }}
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          Set Up MFA Now
        </button>
        <button
          onClick={onSkip}
          className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 transition-colors"
        >
          Skip for Now
        </button>
      </div>

      <p className="text-sm text-gray-500">
        You can set up MFA later from your account settings
      </p>
    </div>
  );

  const renderSetupStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Scan QR Code
        </h2>
        <p className="text-gray-600">
          Open Microsoft Authenticator and scan this QR code
        </p>
      </div>

      {setupData && (
        <div className="space-y-6">
          <div className="flex justify-center p-6 bg-white border-2 border-gray-200 rounded-lg">
            <QRCode value={setupData.qrCodeUrl} size={200} />
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium text-gray-900">Manual Entry Key</h3>
              <button
                onClick={() => copyToClipboard(setupData.manualEntryKey)}
                className="flex items-center text-blue-600 hover:text-blue-700"
              >
                {copiedKey ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <code className="text-sm bg-white px-2 py-1 rounded border break-all">
              {setupData.manualEntryKey}
            </code>
            <p className="text-xs text-gray-500 mt-2">
              Use this if you can't scan the QR code
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => setStep('intro')}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <button
          onClick={() => setStep('verify')}
          disabled={!setupData}
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to Verification
        </button>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Enter Verification Code
        </h2>
        <p className="text-gray-600">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <input
          type="text"
          value={verificationCode}
          onChange={(e) => {
            setError(null);
            setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
          }}
          placeholder="000000"
          className="w-full text-center text-2xl font-mono py-4 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          maxLength={6}
        />
        <p className="text-sm text-gray-500 text-center mt-2">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setStep('setup')}
          className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <button
          onClick={verifyAndEnable}
          disabled={verificationCode.length !== 6 || loading}
          className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify & Enable MFA'
          )}
        </button>
      </div>
    </div>
  );

  const renderBackupStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <Check className="h-6 w-6 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Save Your Backup Codes
        </h2>
        <p className="text-gray-600">
          Store these codes in a safe place. You can use them to access your account if you lose your device.
        </p>
      </div>

      {setupData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-yellow-900">Backup Codes</h3>
            <button
              onClick={() => setShowBackupCodes(!showBackupCodes)}
              className="flex items-center text-yellow-700 hover:text-yellow-800"
            >
              {showBackupCodes ? (
                <>
                  <EyeOff className="h-4 w-4 mr-1" />
                  Hide
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-1" />
                  Show
                </>
              )}
            </button>
          </div>
          
          {showBackupCodes && (
            <div className="grid grid-cols-2 gap-2">
              {setupData.backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white p-2 rounded border"
                >
                  <code className="text-sm font-mono">{code}</code>
                  <button
                    onClick={() => copyToClipboard(code, index)}
                    className="ml-2 text-yellow-600 hover:text-yellow-700"
                  >
                    {copiedCodes[index] ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-sm text-yellow-700 mt-3">
            ⚠️ Each backup code can only be used once. Keep them secure!
          </p>
        </div>
      )}

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-medium text-green-900 mb-2">Setup Complete!</h3>
        <p className="text-green-700 text-sm">
          Multi-factor authentication has been successfully enabled for your account. 
          You'll now need to enter a code from your authenticator app when logging in.
        </p>
      </div>

      <button
        onClick={onComplete}
        className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700"
      >
        Continue to Dashboard
      </button>
    </div>
  );

  if (loading && step === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Setting up MFA...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && step === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center mb-6">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Setup Failed</h2>
              <p className="text-red-600">{error}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onSkip}
                className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200"
              >
                Skip MFA Setup
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setStep('intro');
                }}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white p-8 rounded-xl shadow-lg">
          {step === 'intro' && renderIntroStep()}
          {step === 'setup' && renderSetupStep()}
          {step === 'verify' && renderVerifyStep()}
          {step === 'backup' && renderBackupStep()}
        </div>
      </div>
    </div>
  );
};

export default FirstLoginMFASetup;